import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { fileSizeFormatter } from '../../utils/fileSizeFormatter';

import { FileSelection } from './fileUploadState';
import { UploadFile } from './UploadFile';
import { FileData, isFileSelection } from './uploadTypes';

import styles from './upload-file-section.module.scss';

type FileSectionProps = {
  files: FileData[];
  onRemoveFile: (file: FileData) => void;
  showUploadState: boolean;
  title: string;
  uploadFile?: (file: FileSelection) => Promise<void>;
  metaDataInputLabel?: string;
  metaDataExtractionInput?: (fileData: FileSelection) => React.ReactNode;
};

export const FileSection = ({
  files,
  onRemoveFile,
  showUploadState,
  title,
  uploadFile,
  metaDataExtractionInput,
  metaDataInputLabel,
}: FileSectionProps) => {
  const { t } = useTranslation();
  if (files.length === 0) {
    return null;
  }

  const sortedFiles = [...files].sort((a, b) => {
    if (!(isFileSelection(a) && isFileSelection(b))) {
      return 0;
    } else if (a.uploadState === b.uploadState) {
      return 0;
    } else if (a.uploadState === 'isSuccess') {
      return 1;
    } else if (b.uploadState === 'isSuccess') {
      return -1;
    }

    return 0;
  });

  return (
    <div className={styles['root']}>
      <div className={styles['header-container']}>
        <div className={styles['header-title']}>{title}</div>
        <div className={styles['header-divider']}></div>
        <div
          className={clsx(styles['header-summary'], {
            [styles['header-upload']]: showUploadState,
          })}
        >
          {showUploadState
            ? `${files.reduce((count, current) => {
                if (
                  isFileSelection(current) &&
                  current.uploadState === 'isSuccess'
                ) {
                  return count + 1;
                }
                return count;
              }, 0)}/${files.length}`
            : t('app.upload.fileSummary', '', {
                count: files.length,
                totalFileSize: fileSizeFormatter(
                  files.reduce((prev, curr) => prev + curr.file.size, 0)
                ),
              })}
        </div>
      </div>

      {metaDataExtractionInput && (
        <div className={styles['meta-data-table']}>
          <div className={styles['meta-data-header']}>
            <div className={styles['meta-data-column']}>
              {t('app.upload.extractMetaData.fileName')}
            </div>
            <div className={styles['meta-data-column']}>
              {metaDataInputLabel}
            </div>
            <div className={styles['meta-data-column']}>
              {t('app.upload.extractMetaData.size')}
            </div>
          </div>
        </div>
      )}

      {sortedFiles.map((fileData, index) => {
        const key = 'id' in fileData ? fileData.id : fileData.file.name + index;
        return (
          <div key={key} className={styles['meta-data-row']}>
            <UploadFile
              key={
                isFileSelection(fileData)
                  ? fileData.id
                  : fileData.file.name + index
              }
              fileData={fileData}
              onRemoveFile={onRemoveFile}
              uploadFile={uploadFile}
              metaDataExtractionInput={metaDataExtractionInput}
            />
          </div>
        );
      })}
    </div>
  );
};
